import { bn } from "@opensea/utils"
import type { FragmentOf } from "gql.tada"
import { readFragment } from "gql.tada"
import { calculateOrderTotals } from "../../../lib/fees"
import { EXPIRATION_IN_MINUTES } from "../../../lib/zod/expiration"
import { LISTING_MAX_DECIMALS, MINIMUM_LISTING_PRICE } from "../constants"
import { convertItemsAndPricingToListingsFragment } from "./helpers.fragments"
import type { Listing, ListingFlowFormValues } from "./types"

/**
 * Convert items and prices to listings
 * @param items - GQL item fragments
 * @param prices - Pricing inputs from create form
 * @returns Listing[]
 */
export const convertItemsAndPricingToListings = ({
  adjustForFees = false,
  items: itemKeys,
  formValues,
  startTime,
}: {
  adjustForFees?: boolean
  items: FragmentOf<typeof convertItemsAndPricingToListingsFragment>[]
  formValues: ListingFlowFormValues
  applyCreatorFees?: boolean
  startTime: Date
}): Listing[] => {
  const { listings, expiration } = formValues
  const items = itemKeys.map(item =>
    readFragment(convertItemsAndPricingToListingsFragment, item),
  )

  return items.map(({ id, tokenId, contractAddress, chain, collection }) => {
    const item = {
      tokenId,
      contractAddress,
      chain,
    }

    const listing = listings.find(l => l.id === id)

    if (!listing) {
      throw new Error(`Listing not found: ${id}`)
    }

    const listingTotal = calculateOrderTotals([listing])

    return {
      item,
      quantity: listing.quantity,
      pricePerItem: {
        unit: adjustForFees
          ? bn(listingTotal.adjustedForFees).div(listing.quantity).toNumber()
          : bn(listingTotal.withoutFees).div(listing.quantity).toNumber(),
        contractAddress: chain.listingCurrency.address as `0x${string}`,
      },
      startTime,
      endTime:
        expiration.expirationType === "custom"
          ? (expiration.customExpiration as Date)
          : new Date(
              startTime.getTime() +
                EXPIRATION_IN_MINUTES[expiration.expirationType] * 60 * 1000,
            ),
      openSeaFeeBasisPoints: collection?.fees.openseaFee?.feeBasisPoints,
      reservedBuyer: formValues.reservedBuyer.resolvedAddress ?? undefined,
    }
  })
}

export const sanitizeListingPrice = (price: number | undefined) => {
  return bn(Math.max(price ?? 0, MINIMUM_LISTING_PRICE))
    .decimalPlaces(LISTING_MAX_DECIMALS)
    .toNumber()
}
